import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  addTimelineEventLink as addTimelineEventLinkClient,
  createTimelineEvent as createTimelineEventClient,
  deleteTimelineEvent as deleteTimelineEventClient,
  formatTimelineErrorMessage,
  getTimelineLimitPrompt,
  reorderTimelineEvents as reorderTimelineEventsClient,
  removeTimelineEventImage as removeTimelineEventImageClient,
  removeTimelineEventLink as removeTimelineEventLinkClient,
  TimelineActionError,
  updateTimelineEvent as updateTimelineEventClient,
} from "@/features/timeline/timeline.client";
import { loadTimelineWorkspace as loadTimelineWorkspaceClient } from "@/features/timeline/timeline.repository";
import {
  approveTimelineSummary as approveTimelineSummaryClient,
  formatTimelineSummaryErrorMessage,
  previewTimelineSummary as previewTimelineSummaryClient,
  TimelineSummaryError,
} from "@/features/timeline/timeline-summary.client";
import {
  applyTimelineEventOrder,
  createTimelineEventDraft,
  type TimelineEventDraft,
  type TimelineEventView,
  type TimelineLimitPrompt,
  type TimelineLinkTargetOption,
  type TimelineSummaryPreview,
  type TimelineSummaryRequestState,
  type TimelineViewMode,
  type TimelineWorkspaceSnapshot,
} from "@/features/timeline";
import { projectShellStore } from "@/stores/projectShellStore";
import { subscriptionStore } from "@/stores/subscriptionStore";
import { createTimelineEventImageSignedUrl } from "@/features/timeline/timelineStorage";

const DEFAULT_NEW_EVENT = createTimelineEventDraft({
  description: "Describe what changed in this moment and why it matters.",
  eraLabel: "New era",
  title: "Untitled event",
});

export interface ProjectTimelineDependencies {
  addTimelineEventLink: typeof addTimelineEventLinkClient;
  approveTimelineSummary: typeof approveTimelineSummaryClient;
  createTimelineEvent: typeof createTimelineEventClient;
  deleteTimelineEvent: typeof deleteTimelineEventClient;
  loadTimelineWorkspace: typeof loadTimelineWorkspaceClient;
  previewTimelineSummary: typeof previewTimelineSummaryClient;
  removeTimelineEventImage: typeof removeTimelineEventImageClient;
  removeTimelineEventLink: typeof removeTimelineEventLinkClient;
  reorderTimelineEvents: typeof reorderTimelineEventsClient;
  updateTimelineEvent: typeof updateTimelineEventClient;
}

export interface ProjectTimelineNotice {
  kind: "error" | "success";
  message: string;
}

export interface ProjectTimelineStoreState {
  applyGeneratedEventImage: (
    eventId: string,
    imagePath: string,
    creditsRemaining?: number | null,
  ) => Promise<void>;
  closeDeleteDialog: () => void;
  createEvent: (draft?: TimelineEventDraft) => Promise<TimelineEventView>;
  deleteDialog: {
    open: boolean;
    submitting: boolean;
    targetEventId: string | null;
  };
  deleteEvent: () => Promise<void>;
  dismissNotice: () => void;
  dismissSummaryPreview: () => void;
  hydrateWorkspace: (projectId: string) => Promise<void>;
  limitPrompt: TimelineLimitPrompt | null;
  linkSelectedEvent: (option: TimelineLinkTargetOption) => Promise<void>;
  notice: ProjectTimelineNotice | null;
  openDeleteDialog: (eventId?: string) => void;
  projectId: string | null;
  refreshWorkspace: () => Promise<void>;
  removeLink: (linkId: string) => Promise<void>;
  removeSelectedEventImage: () => Promise<void>;
  reorderEvents: (orderedEventIds: string[]) => Promise<void>;
  saveSelectedEvent: (draft: TimelineEventDraft) => Promise<TimelineEventView>;
  selectEvent: (eventId: string | null) => void;
  selectedEventId: string | null;
  setViewMode: (viewMode: TimelineViewMode) => void;
  status: "idle" | "loading" | "ready" | "error";
  summaryError: TimelineSummaryError | null;
  summaryPreview: TimelineSummaryPreview | null;
  summaryRequestState: TimelineSummaryRequestState;
  viewMode: TimelineViewMode;
  workspace: TimelineWorkspaceSnapshot | null;
  approveSelectedEventSummary: () => Promise<void>;
  previewSelectedEventSummary: () => Promise<void>;
}

const defaultDependencies: ProjectTimelineDependencies = {
  addTimelineEventLink: addTimelineEventLinkClient,
  approveTimelineSummary: approveTimelineSummaryClient,
  createTimelineEvent: createTimelineEventClient,
  deleteTimelineEvent: deleteTimelineEventClient,
  loadTimelineWorkspace: loadTimelineWorkspaceClient,
  previewTimelineSummary: previewTimelineSummaryClient,
  removeTimelineEventImage: removeTimelineEventImageClient,
  removeTimelineEventLink: removeTimelineEventLinkClient,
  reorderTimelineEvents: reorderTimelineEventsClient,
  updateTimelineEvent: updateTimelineEventClient,
};

function getTimelineNoticeMessage(error: unknown) {
  return error instanceof TimelineSummaryError
    ? formatTimelineSummaryErrorMessage(error)
    : formatTimelineErrorMessage(error);
}

function getTimelineSummaryError(error: unknown) {
  if (error instanceof TimelineSummaryError) {
    return error;
  }

  return new TimelineSummaryError("failed", getTimelineNoticeMessage(error));
}

function refreshProjectSummary(projectId: string) {
  void projectShellStore.getState().hydrateProjectDashboard(projectId);
}

export function createProjectTimelineStore(
  deps: ProjectTimelineDependencies = defaultDependencies,
) {
  return createStore<ProjectTimelineStoreState>((set, get) => {
    async function hydrate(projectId: string, preferredSelectedId: string | null = null) {
      const workspace = await deps.loadTimelineWorkspace(projectId);
      const selectedEventId = workspace.events.some((event) => event.id === preferredSelectedId)
        ? preferredSelectedId
        : null;

      set((state) => ({
        deleteDialog: {
          open: false,
          submitting: false,
          targetEventId: null,
        },
        limitPrompt: null,
        notice: state.notice?.kind === "success" ? state.notice : null,
        projectId,
        selectedEventId,
        status: "ready",
        summaryError:
          state.summaryPreview?.eventId === selectedEventId ? state.summaryError : null,
        summaryPreview:
          state.summaryPreview?.eventId === selectedEventId ? state.summaryPreview : null,
        workspace,
      }));
    }

    return {
      deleteDialog: {
        open: false,
        submitting: false,
        targetEventId: null,
      },
      limitPrompt: null,
      notice: null,
      projectId: null,
      selectedEventId: null,
      status: "idle",
      summaryError: null,
      summaryPreview: null,
      summaryRequestState: "idle",
      viewMode: "list",
      workspace: null,

      applyGeneratedEventImage: async (eventId, imagePath, creditsRemaining = null) => {
        const imageUrl = await createTimelineEventImageSignedUrl(imagePath).catch(() => null);

        if (typeof creditsRemaining === "number") {
          subscriptionStore.getState().setCreditBalance(creditsRemaining);
        }

        set((state) => ({
          workspace: state.workspace
            ? {
                ...state.workspace,
                events: state.workspace.events.map((event) =>
                  event.id === eventId
                    ? {
                        ...event,
                        imagePath,
                        imageUrl,
                        updatedAt: new Date().toISOString(),
                      }
                    : event,
                ),
              }
            : null,
        }));
      },

      hydrateWorkspace: async (projectId) => {
        set({
          deleteDialog: {
            open: false,
            submitting: false,
            targetEventId: null,
          },
          limitPrompt: null,
          notice: null,
          projectId,
          selectedEventId: null,
          status: "loading",
          summaryError: null,
          summaryPreview: null,
          summaryRequestState: "idle",
          workspace: null,
        });

        try {
          await hydrate(projectId);
        } catch (error) {
          set({
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
            status: "error",
          });
        }
      },

      refreshWorkspace: async () => {
        const projectId = get().projectId;
        if (!projectId) {
          return;
        }

        try {
          await hydrate(projectId, get().selectedEventId);
        } catch (error) {
          set({
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
            status: "error",
          });
        }
      },

      selectEvent: (eventId) => {
        set((state) => ({
          selectedEventId: eventId,
          summaryError: null,
          summaryPreview:
            state.summaryPreview?.eventId === eventId ? state.summaryPreview : null,
        }));
      },

      setViewMode: (viewMode) => {
        set({
          viewMode,
        });
      },

      createEvent: async (draft = DEFAULT_NEW_EVENT) => {
        const projectId = get().projectId;
        if (!projectId) {
          throw new TimelineActionError("not-found", "That project is unavailable.");
        }

        try {
          const created = await deps.createTimelineEvent(projectId, draft);
          await hydrate(projectId, created.id);
          refreshProjectSummary(projectId);

          set({
            notice: {
              kind: "success",
              message: "timeline.messages.createSuccess",
            },
          });

          const createdEvent = get().workspace?.events.find((event) => event.id === created.id);
          if (!createdEvent) {
            throw new TimelineActionError("not-found", "That timeline event is unavailable.");
          }

          return createdEvent;
        } catch (error) {
          set({
            limitPrompt: getTimelineLimitPrompt(error),
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
          });
          throw error;
        }
      },

      saveSelectedEvent: async (draft) => {
        const projectId = get().projectId;
        const selectedEventId = get().selectedEventId;

        if (!projectId || !selectedEventId) {
          throw new TimelineActionError("not-found", "That timeline event is unavailable.");
        }

        try {
          await deps.updateTimelineEvent(selectedEventId, draft);
          await hydrate(projectId, selectedEventId);

          set({
            notice: {
              kind: "success",
              message: "timeline.messages.saveSuccess",
            },
          });

          const updatedEvent = get().workspace?.events.find((event) => event.id === selectedEventId);
          if (!updatedEvent) {
            throw new TimelineActionError("not-found", "That timeline event is unavailable.");
          }

          return updatedEvent;
        } catch (error) {
          set({
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
          });
          throw error;
        }
      },

      linkSelectedEvent: async (option) => {
        const projectId = get().projectId;
        const selectedEventId = get().selectedEventId;

        if (!projectId || !selectedEventId) {
          throw new TimelineActionError("not-found", "That timeline event is unavailable.");
        }

        try {
          await deps.addTimelineEventLink(selectedEventId, option);
          await hydrate(projectId, selectedEventId);
        } catch (error) {
          set({
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
          });
          throw error;
        }
      },

      removeLink: async (linkId) => {
        const projectId = get().projectId;
        const selectedEventId = get().selectedEventId;

        if (!projectId || !selectedEventId) {
          throw new TimelineActionError("not-found", "That timeline event is unavailable.");
        }

        try {
          await deps.removeTimelineEventLink(linkId);
          await hydrate(projectId, selectedEventId);
        } catch (error) {
          set({
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
          });
          throw error;
        }
      },

      removeSelectedEventImage: async () => {
        const selectedEventId = get().selectedEventId;
        const workspace = get().workspace;
        const selectedEvent = workspace?.events.find((event) => event.id === selectedEventId);

        if (!selectedEventId || !selectedEvent?.imagePath) {
          throw new TimelineActionError("not-found", "That timeline event image is unavailable.");
        }

        try {
          await deps.removeTimelineEventImage(selectedEventId, selectedEvent.imagePath);
          set((state) => ({
            workspace: state.workspace
              ? {
                  ...state.workspace,
                  events: state.workspace.events.map((event) =>
                    event.id === selectedEventId
                      ? {
                          ...event,
                          imagePath: null,
                          imageUrl: null,
                          updatedAt: new Date().toISOString(),
                        }
                      : event,
                  ),
                }
              : null,
          }));
        } catch (error) {
          set({
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
          });
          throw error;
        }
      },

      reorderEvents: async (orderedEventIds) => {
        const projectId = get().projectId;
        const workspace = get().workspace;

        if (!projectId || !workspace) {
          throw new TimelineActionError("not-found", "That timeline workspace is unavailable.");
        }

        set({
          workspace: {
            ...workspace,
            events: applyTimelineEventOrder(workspace.events, orderedEventIds),
          },
        });

        try {
          await deps.reorderTimelineEvents(projectId, orderedEventIds);
        } catch (error) {
          await hydrate(projectId, get().selectedEventId);
          set({
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
          });
          throw error;
        }
      },

      openDeleteDialog: (eventId) => {
        set((state) => ({
          deleteDialog: {
            open: true,
            submitting: false,
            targetEventId: eventId ?? state.selectedEventId,
          },
        }));
      },

      closeDeleteDialog: () => {
        set({
          deleteDialog: {
            open: false,
            submitting: false,
            targetEventId: null,
          },
        });
      },

      deleteEvent: async () => {
        const projectId = get().projectId;
        const targetEventId = get().deleteDialog.targetEventId;

        if (!projectId || !targetEventId) {
          throw new TimelineActionError("not-found", "That timeline event is unavailable.");
        }

        set((state) => ({
          deleteDialog: {
            ...state.deleteDialog,
            submitting: true,
          },
        }));

        try {
          await deps.deleteTimelineEvent(targetEventId);
          await hydrate(projectId, null);
          refreshProjectSummary(projectId);

          set({
            notice: {
              kind: "success",
              message: "timeline.messages.deleteSuccess",
            },
          });
        } catch (error) {
          set((state) => ({
            deleteDialog: {
              ...state.deleteDialog,
              submitting: false,
            },
            notice: {
              kind: "error",
              message: getTimelineNoticeMessage(error),
            },
          }));
          throw error;
        }
      },

      dismissNotice: () => {
        set({
          notice: null,
        });
      },

      dismissSummaryPreview: () => {
        set({
          summaryError: null,
          summaryPreview: null,
          summaryRequestState: "idle",
        });
      },

      previewSelectedEventSummary: async () => {
        const projectId = get().projectId;
        const selectedEventId = get().selectedEventId;

        if (!projectId || !selectedEventId) {
          throw new TimelineActionError("not-found", "That timeline event is unavailable.");
        }

        set({
          summaryError: null,
          summaryPreview: null,
          summaryRequestState: "previewing",
        });

        try {
          const preview = await deps.previewTimelineSummary(projectId, selectedEventId);
          set({
            summaryPreview: preview,
            summaryRequestState: "idle",
          });
        } catch (error) {
          set({
            summaryError: getTimelineSummaryError(error),
            summaryRequestState: "idle",
          });
          throw error;
        }
      },

      approveSelectedEventSummary: async () => {
        const projectId = get().projectId;
        const summaryPreview = get().summaryPreview;

        if (!projectId || !summaryPreview) {
          throw new TimelineActionError("not-found", "That summary preview is unavailable.");
        }

        set({
          summaryError: null,
          summaryRequestState: "approving",
        });

        try {
          await deps.approveTimelineSummary(
            projectId,
            summaryPreview.eventId,
            summaryPreview.approvalToken,
          );
          await hydrate(projectId, summaryPreview.eventId);
          await subscriptionStore.getState().refreshCredits().catch(() => undefined);
          set({
            notice: {
              kind: "success",
              message: "timeline.summary.approvedSuccess",
            },
            summaryError: null,
            summaryPreview: null,
            summaryRequestState: "idle",
          });
        } catch (error) {
          set({
            summaryError: getTimelineSummaryError(error),
            summaryRequestState: "idle",
            summaryPreview:
              error instanceof TimelineSummaryError && error.code === "stale"
                ? null
                : get().summaryPreview,
          });
          throw error;
        }
      },
    };
  });
}

export const projectTimelineStore = createProjectTimelineStore();

export function useProjectTimelineStore<T>(
  selector: (state: ProjectTimelineStoreState) => T,
) {
  return useStore(projectTimelineStore, selector);
}

export function selectActiveTimelineEvent(
  state: Pick<ProjectTimelineStoreState, "selectedEventId" | "workspace">,
) {
  return state.workspace?.events.find((event) => event.id === state.selectedEventId) ?? null;
}
